import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SsnInput from "@/components/SsnInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Download, FileText, AlertCircle, ChevronLeft, ChevronRight, X,
  Check, Loader2, StickyNote, User as UserIcon, MapPin, Phone,
  IdCard, Calendar, Hash, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCcw, ShieldOff,
  Award, Save, Heart,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/config";
import { detectKind } from "./DocumentPreviewDialog";
import { formatPhoneFr, upperLastName } from "@/lib/formatters";
import PhoneInputFr from "@/components/PhoneInputFr";
import { api } from "./adminApi";
import { CACES_CATEGORIES } from "@/constants";
import NationalitySelect from "@/components/NationalitySelect";
import { isEuEeaCh } from "@/lib/eu_nationalities";
import CVAnonymizer from "./CVAnonymizer";

// -------- Sub-component: DocValidationBadge (Accepter / Refuser / Manquant) --
function DocValidationBadge({ doc, onChanged }) {
  const [status, setStatus] = useState(doc?.validation_status || "pending");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setStatus(doc?.validation_status || "pending"); }, [doc?.doc_id, doc?.validation_status]);
  const setValidation = async (next) => {
    if (!doc?.doc_id) return;
    setSaving(true);
    try {
      let comment = "";
      if (next === "rejected") {
        comment = window.prompt("Motif de refus (facultatif)") || "";
      }
      const r = await api.patch(`/admin/documents/${doc.doc_id}/validation`, { status: next, comment });
      setStatus(r.data.validation_status);
      onChanged?.();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Erreur validation");
    } finally {
      setSaving(false);
    }
  };
  const btnCls = "text-[10px] h-7 px-2 rounded-none font-semibold";
  return (
    <div className="flex items-center gap-1 ml-1" data-testid={`doc-validation-${doc.doc_id}`}>
      {saving && <Loader2 className="w-3 h-3 animate-spin text-[#662483]" />}
      <button
        onClick={() => setValidation("accepted")}
        className={`${btnCls} ${status === "accepted" ? "bg-green-600 text-white" : "bg-white border border-green-500 text-green-700 hover:bg-green-50"}`}
        title="Accepter"
        data-testid={`doc-validate-accept-${doc.doc_id}`}
      >✓ Accepter</button>
      <button
        onClick={() => setValidation("rejected")}
        className={`${btnCls} ${status === "rejected" ? "bg-red-600 text-white" : "bg-white border border-red-500 text-red-700 hover:bg-red-50"}`}
        title="Refuser"
        data-testid={`doc-validate-reject-${doc.doc_id}`}
      >✗ Refuser</button>
    </div>
  );
}

const DOC_TYPE_LABELS = {
  cv: "CV",
  id_card: "Pièce d'identité",
  id_card_recto: "CNI / Passeport — recto",
  id_card_verso: "CNI — verso",
  carte_sejour: "Carte de séjour",
  carte_sejour_recto: "Carte de séjour — recto",
  carte_sejour_verso: "Carte de séjour — verso",
  attestation_secu: "Attestation Sécu",
  rib: "RIB",
  justificatif_domicile: "Justificatif domicile",
  certificat_travail: "Certificat travail / bulletins",
  visite_medicale: "Visite médicale",
  rqth: "RQTH",
  caces_habilitations: "CACES / habilitations",
  other: "Autre",
};

// Subset of editable fields shown in the side panel — short labels for the cramped UI.
const EDITABLE_FIELDS = [
  { key: "notes_admin", label: "Notes admin", icon: StickyNote, type: "textarea", rows: 5, highlight: true, placeholder: "Vos remarques pendant la lecture du CV…" },
  { key: "phone", label: "Téléphone", icon: Phone, type: "phone" },
  { key: "email", label: "Email", icon: null, type: "email" },
  { key: "address", label: "Adresse", icon: MapPin, type: "text" },
  { key: "postal_code", label: "CP", icon: null, type: "text", short: true },
  { key: "city", label: "Ville", icon: null, type: "text", short: true },
  { key: "birth_date", label: "Date de naissance", icon: Calendar, type: "date" },
  { key: "birth_place", label: "Lieu de naissance", icon: null, type: "text" },
  { key: "nationality", label: "Nationalité", icon: null, type: "nationality" },
  { key: "social_security_number", label: "N° Sécurité sociale", icon: Hash, type: "ssn" },
  { key: "experience", label: "Expérience", icon: null, type: "textarea", rows: 3 },
  // Contact d'urgence — bloc dédié
  { key: "emergency_contact_name", label: "Contact urgence — nom", icon: Heart, type: "text" },
  { key: "emergency_contact_phone", label: "Contact urgence — tél", icon: Phone, type: "phone" },
  { key: "emergency_contact_relation", label: "Contact urgence — lien", icon: null, type: "relation" },
];

/**
 * Split-screen document preview + live-editable candidate side panel.
 * - 2/3 left: PDF/image preview (with prev/next navigation across all documents)
 * - 1/3 right: candidate fields, Notes Admin pinned at top, autosave on blur
 *
 * Props:
 *   candidate: full candidate doc (with documents[] embedded)
 *   doc: the document object currently focused (used to pick initial index)
 *   open / onOpenChange: dialog state
 *   onSaved(updatedCandidate): called after every successful autosave
 */
export default function CandidateSplitPreviewDialog({ candidate, doc, open, onOpenChange, onSaved }) {
  const list = candidate?.documents?.filter((d) => !d.is_deleted) || [];
  const initialIndex = doc ? Math.max(0, list.findIndex((d) => d.doc_id === doc.doc_id)) : 0;
  const [index, setIndex] = useState(initialIndex);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  // Zoom & pan for images
  const [imgZoom, setImgZoom] = useState(1);
  const [imgPan, setImgPan] = useState({ x: 0, y: 0 });
  const panState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  // Anonymization dialog
  const [anonymizerOpen, setAnonymizerOpen] = useState(false);

  useEffect(() => { if (open) setIndex(initialIndex); }, [open, initialIndex]);

  const current = list[index];
  const { kind, mime } = detectKind(current?.original_filename || "");

  // Reset zoom/pan whenever document changes
  useEffect(() => { setImgZoom(1); setImgPan({ x: 0, y: 0 }); }, [current?.doc_id]);

  // Image pan handlers
  const onImgMouseDown = (e) => {
    if (imgZoom <= 1) return;
    panState.current = { dragging: true, startX: e.clientX, startY: e.clientY, origX: imgPan.x, origY: imgPan.y };
  };
  const onImgMouseMove = (e) => {
    if (!panState.current.dragging) return;
    setImgPan({
      x: panState.current.origX + (e.clientX - panState.current.startX),
      y: panState.current.origY + (e.clientY - panState.current.startY),
    });
  };
  const onImgMouseUp = () => { panState.current.dragging = false; };
  const onImgWheel = (e) => {
    if (kind !== "image") return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setImgZoom((z) => Math.min(5, Math.max(1, +(z + delta).toFixed(2))));
  };
  const zoomIn = () => setImgZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)));
  const zoomOut = () => setImgZoom((z) => {
    const next = Math.max(1, +(z - 0.25).toFixed(2));
    if (next === 1) setImgPan({ x: 0, y: 0 });
    return next;
  });
  const zoomReset = () => { setImgZoom(1); setImgPan({ x: 0, y: 0 }); };

  // Preview blob fetch -------------------------------------------------------
  useEffect(() => {
    if (!open || !current) return;
    if (kind !== "image" && kind !== "pdf") { setBlobUrl(null); return; }
    let url = null;
    let cancelled = false;
    setLoading(true);
    setError("");
    setBlobUrl(null);
    axios.get(`${API}/documents/${current.doc_id}/download`, { withCredentials: true, responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const blob = new Blob([r.data], { type: mime || r.headers["content-type"] || "application/octet-stream" });
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch(() => { if (!cancelled) setError("Impossible de charger l'aperçu"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [open, current, kind, mime]);

  const prev = useCallback(() => { if (list.length > 1) setIndex((i) => (i - 1 + list.length) % list.length); }, [list.length]);
  const next = useCallback(() => { if (list.length > 1) setIndex((i) => (i + 1) % list.length); }, [list.length]);

  // Keyboard nav (arrows + escape).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft" && document.activeElement?.tagName !== "TEXTAREA" && document.activeElement?.tagName !== "INPUT") prev();
      else if (e.key === "ArrowRight" && document.activeElement?.tagName !== "TEXTAREA" && document.activeElement?.tagName !== "INPUT") next();
      else if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next, onOpenChange]);

  // Side panel state + autosave ---------------------------------------------
  const [form, setForm] = useState({});
  const [savingField, setSavingField] = useState(null);
  const [savedFlash, setSavedFlash] = useState(null);
  const baselineRef = useRef({});

  // Reset form when candidate changes
  useEffect(() => {
    if (!candidate) return;
    const f = {};
    EDITABLE_FIELDS.forEach((field) => { f[field.key] = candidate[field.key] ?? ""; });
    setForm(f);
    baselineRef.current = { ...f };
  }, [candidate?.user_id, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistField = async (key, value) => {
    // Skip when value did not actually change (avoid noisy network on every focus loss)
    if (baselineRef.current[key] === value) return;
    setSavingField(key);
    try {
      let payload = { [key]: value };
      // Apply backend-side conventions client-side too, so the badge matches
      if (key === "phone") payload[key] = formatPhoneFr(value);
      const r = await api.patch(`/candidates/${candidate.user_id}/profile`, payload);
      baselineRef.current = { ...baselineRef.current, [key]: value };
      setSavedFlash(key);
      setTimeout(() => setSavedFlash((k) => (k === key ? null : k)), 1500);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Erreur de sauvegarde (${key})`);
    } finally {
      setSavingField((s) => (s === key ? null : s));
    }
  };

  const handleBlur = (key) => persistField(key, form[key]);
  const setField = (key, v) => setForm((f) => ({ ...f, [key]: v }));

  // Après une anonymisation PDF réussie : le nouveau document "ANON - ..."
  // est créé côté serveur mais `candidate` (prop figée) ne le contient pas
  // encore — on recharge la fiche pour que `list` (dérivée de
  // candidate.documents) l'inclue immédiatement, sans que l'admin ait à
  // fermer/rouvrir la fiche candidat.
  const handleAnonymized = async () => {
    try {
      const r = await api.get(`/candidates/${candidate.user_id}`);
      onSaved?.(r.data);
    } catch (err) {
      toast.error("PDF anonymisé enregistré, mais la liste n'a pas pu être rafraîchie automatiquement — rouvrez la fiche pour le voir.");
    }
  };

  const handleDownload = () => {
    if (!current) return;
    const a = document.createElement("a");
    a.href = `${API}/documents/${current.doc_id}/download`;
    a.click();
  };

  if (!candidate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`rounded-none p-0 flex flex-col overflow-hidden ${expanded ? "max-w-[98vw] h-[97vh]" : "max-w-[95vw] h-[93vh] sm:max-w-[1500px]"}`}
        data-testid="split-preview-dialog"
      >
        <DialogTitle className="sr-only">Aperçu des documents — {candidate.name}</DialogTitle>
        <DialogDescription className="sr-only">Visualisation des pièces jointes du candidat avec édition rapide de la fiche.</DialogDescription>
        {/* Sticky top bar */}
        <div className="bg-gradient-to-r from-[#662483] to-[#0069B4] px-4 py-2.5 flex items-center gap-3 text-white shrink-0">
          <UserIcon className="w-4 h-4 shrink-0" />
          <span className="font-bold truncate" data-testid="split-candidate-name">{candidate.name}</span>
          <Badge className="rounded-none bg-white/15 text-white border-0 text-[10px] shrink-0">
            {candidate.role === "employee" ? "Salarié" : "Candidat"}
          </Badge>
          <span className="text-xs opacity-80 truncate hidden md:inline">
            {current?.original_filename || "—"}
          </span>
          <span className="text-xs opacity-80 hidden sm:inline ml-auto">
            {list.length > 0 ? `${index + 1} / ${list.length}` : ""}
          </span>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1.5 hover:bg-white/15 transition-colors"
            title={expanded ? "Réduire" : "Plein écran"}
            data-testid="split-toggle-expand"
          >
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1.5 hover:bg-white/15 transition-colors"
            title="Fermer (Échap)"
            data-testid="split-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body : 2/3 preview + 1/3 edit panel */}
        <div className="flex-1 flex min-h-0">
          {/* LEFT — preview */}
          <div className="flex-[2] min-w-0 flex flex-col bg-[#FAFAFA] border-r border-gray-200">
            {/* Preview navigation strip */}
            <div className="bg-white border-b border-gray-200 px-3 py-1.5 flex items-center gap-2 shrink-0">
              <button
                onClick={prev}
                disabled={list.length < 2}
                className="p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="split-prev-doc"
                title="Document précédent (←)"
              >
                <ChevronLeft className="w-4 h-4 text-[#3F3F3F]" />
              </button>
              <button
                onClick={next}
                disabled={list.length < 2}
                className="p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="split-next-doc"
                title="Document suivant (→)"
              >
                <ChevronRight className="w-4 h-4 text-[#3F3F3F]" />
              </button>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="text-xs font-medium text-[#3F3F3F] truncate">{current?.original_filename || "Aucun document"}</span>
                {current && (
                  <span className="text-[10px] font-bold text-[#662483] bg-[#F0E9F3] px-2 py-0.5 shrink-0">
                    {DOC_TYPE_LABELS[current.doc_type] || current.doc_type}
                  </span>
                )}
              </div>
              <Button onClick={handleDownload} variant="outline" disabled={!current} className="rounded-none text-xs h-7" data-testid="split-download">
                <Download className="w-3 h-3 mr-1" /> Télécharger
              </Button>
              {/* Anonymisation — always visible for CV documents (the dialog
                  itself handles unsupported PDF formats with a clear message).
                  For non-CV image documents, still available. */}
              {current && (kind === "image" || current.doc_type === "cv") && (
                <Button
                  onClick={() => setAnonymizerOpen(true)}
                  variant="outline"
                  className="rounded-none text-xs h-7 border-[#662483] text-[#662483] hover:bg-[#F0E9F3]"
                  data-testid="split-anonymize-btn"
                  title="Masquer des zones du document avant transmission client"
                >
                  <ShieldOff className="w-3 h-3 mr-1" /> Anonymiser
                </Button>
              )}
              {/* Zoom controls (image only) */}
              {kind === "image" && (
                <div className="flex items-center gap-0.5 border-l border-gray-200 pl-2 ml-1" data-testid="image-zoom-controls">
                  <button onClick={zoomOut} disabled={imgZoom <= 1} className="p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Dézoomer" data-testid="image-zoom-out">
                    <ZoomOut className="w-3.5 h-3.5 text-[#3F3F3F]" />
                  </button>
                  <span className="text-[10px] font-bold text-[#3F3F3F] w-9 text-center tabular-nums" data-testid="image-zoom-value">{Math.round(imgZoom * 100)}%</span>
                  <button onClick={zoomIn} disabled={imgZoom >= 5} className="p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Zoomer" data-testid="image-zoom-in">
                    <ZoomIn className="w-3.5 h-3.5 text-[#3F3F3F]" />
                  </button>
                  <button onClick={zoomReset} disabled={imgZoom === 1} className="p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Réinitialiser" data-testid="image-zoom-reset">
                    <RotateCcw className="w-3.5 h-3.5 text-[#3F3F3F]" />
                  </button>
                </div>
              )}
              {current && <DocValidationBadge doc={current} onChanged={() => onSaved?.({})} />}
            </div>

            {/* Preview area */}
            <div
              className="flex-1 overflow-hidden flex items-center justify-center relative"
              onWheel={onImgWheel}
              onMouseMove={onImgMouseMove}
              onMouseUp={onImgMouseUp}
              onMouseLeave={onImgMouseUp}
            >
              {!current && (
                <p className="text-sm text-gray-500 italic">Aucun document à prévisualiser.</p>
              )}
              {loading && <Loader2 className="w-6 h-6 animate-spin text-[#662483]" />}
              {error && (
                <div className="text-center p-6">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              {!loading && !error && kind === "image" && blobUrl && (
                <img
                  src={blobUrl}
                  alt={current?.original_filename}
                  className="max-w-full max-h-full object-contain select-none"
                  style={{
                    transform: `translate(${imgPan.x}px, ${imgPan.y}px) scale(${imgZoom})`,
                    transformOrigin: "center center",
                    cursor: imgZoom > 1 ? (panState.current.dragging ? "grabbing" : "grab") : "default",
                    transition: panState.current.dragging ? "none" : "transform 120ms ease-out",
                  }}
                  onMouseDown={onImgMouseDown}
                  onDoubleClick={() => (imgZoom === 1 ? setImgZoom(2) : zoomReset())}
                  draggable={false}
                  data-testid="split-preview-image"
                />
              )}
              {!loading && !error && kind === "pdf" && blobUrl && (
                <iframe
                  src={`${blobUrl}#toolbar=1&navpanes=0`}
                  title={current?.original_filename}
                  className="w-full h-full border-0"
                  data-testid="split-preview-iframe"
                />
              )}
              {(kind === "office" || kind === "other") && current && !loading && (
                <div className="text-center p-8 max-w-md">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm font-semibold text-[#3F3F3F] mb-1">Aperçu non disponible</p>
                  <p className="text-xs text-gray-500 mb-4">Ce type de fichier ne peut pas être affiché dans le navigateur.</p>
                  <Button onClick={handleDownload} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none">
                    <Download className="w-4 h-4 mr-2" /> Télécharger
                  </Button>
                </div>
              )}
            </div>

            {/* Document thumbnails */}
            {list.length > 1 && (
              <div className="flex items-center gap-1 px-3 py-2 border-t border-gray-200 bg-white overflow-x-auto shrink-0">
                {list.map((d, i) => (
                  <button
                    key={d.doc_id}
                    onClick={() => setIndex(i)}
                    className={`text-[10px] px-2 py-1 border whitespace-nowrap transition-colors ${i === index ? "bg-[#662483] text-white border-[#662483]" : "bg-white text-gray-600 border-gray-200 hover:border-[#662483]"}`}
                    data-testid={`split-thumb-${i}`}
                    title={d.original_filename}
                  >
                    {DOC_TYPE_LABELS[d.doc_type] || d.doc_type}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — editable side panel */}
          <aside className="flex-1 min-w-[340px] max-w-[460px] overflow-y-auto bg-white" data-testid="split-edit-panel">
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-bold text-[#3F3F3F]">Fiche candidat</h3>
                <span className="text-[10px] text-gray-500">Sauvegarde auto</span>
              </div>

              {/* Rename current document — always visible, admins & candidates */}
              {current && (
                <RenameDocBlock
                  doc={current}
                  onRenamed={(newName) => {
                    // update the in-memory doc so the thumbnail label stays in sync
                    if (candidate?.documents) {
                      const idx = candidate.documents.findIndex((d) => d.doc_id === current.doc_id);
                      if (idx >= 0) candidate.documents[idx].original_filename = newName;
                    }
                    // Also refresh via parent callback (best-effort)
                    onSaved?.(candidate);
                  }}
                />
              )}

              {/* CACES expiry inline — only when the current document is CACES */}
              {current?.doc_type === "caces_habilitations" && (
                <CacesExpiryInline
                  candidate={candidate}
                  onSaved={(updated) => onSaved?.(updated)}
                />
              )}

              {EDITABLE_FIELDS.map((field) => {
                const Icon = field.icon;
                const isSaving = savingField === field.key;
                const justSaved = savedFlash === field.key;
                return (
                  <div key={field.key} className={field.short ? "" : ""}>
                    <Label className={`text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1 ${field.highlight ? "text-[#662483]" : "text-gray-600"}`}>
                      {Icon && <Icon className="w-3 h-3" />}
                      {field.label}
                      {isSaving && <Loader2 className="w-3 h-3 animate-spin ml-1 text-[#662483]" />}
                      {justSaved && <Check className="w-3 h-3 ml-1 text-green-600" data-testid={`split-saved-${field.key}`} />}
                    </Label>
                    {field.type === "textarea" ? (
                      <Textarea
                        value={form[field.key] || ""}
                        onChange={(e) => setField(field.key, e.target.value)}
                        onBlur={() => handleBlur(field.key)}
                        rows={field.rows || 3}
                        placeholder={field.placeholder || ""}
                        className={`rounded-none text-sm ${field.highlight ? "border-[#662483] focus:ring-[#662483]" : ""}`}
                        data-testid={`split-field-${field.key}`}
                      />
                    ) : field.type === "nationality" ? (
                      <NationalitySelect
                        value={form[field.key] || ""}
                        onChange={(v) => { setField(field.key, v); persistField(field.key, v); }}
                        dataTestId={`split-field-${field.key}`}
                      />
                    ) : field.type === "ssn" ? (
                      <SsnInput
                        value={form[field.key] || ""}
                        onChange={(v) => { setField(field.key, v); persistField(field.key, v); }}
                        civility={candidate.civility || ""}
                        birthDateIso={form.birth_date || candidate.birth_date || ""}
                        testid={`split-field-${field.key}`}
                        showValidation={false}
                      />
                    ) : field.type === "relation" ? (
                      <select
                        value={form[field.key] || ""}
                        onChange={(e) => { setField(field.key, e.target.value); persistField(field.key, e.target.value); }}
                        className="w-full h-9 rounded-none border border-gray-300 bg-white px-3 text-sm"
                        data-testid={`split-field-${field.key}`}
                      >
                        <option value="">—</option>
                        <option value="conjoint">Conjoint(e)</option>
                        <option value="parent">Parent</option>
                        <option value="enfant">Enfant</option>
                        <option value="fratrie">Frère / Sœur</option>
                        <option value="ami">Ami(e)</option>
                        <option value="autre">Autre</option>
                      </select>
                    ) : field.type === "phone" ? (
                      <PhoneInputFr
                        value={form[field.key] || ""}
                        onChange={(v) => setField(field.key, v)}
                        onBlur={() => handleBlur(field.key)}
                        placeholder="06 12 34 56 78"
                        data-testid={`split-field-${field.key}`}
                      />
                    ) : (
                      <Input
                        type={field.type === "email" ? "email" : (field.type === "date" ? "date" : "text")}
                        value={form[field.key] || ""}
                        onChange={(e) => setField(field.key, e.target.value)}
                        onBlur={() => handleBlur(field.key)}
                        className="rounded-none text-sm h-9"
                        data-testid={`split-field-${field.key}`}
                      />
                    )}
                  </div>
                );
              })}

              {/* Civilité + Last name in upper case (kept after the main fields) */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-600">Civilité</Label>
                  <Select
                    value={candidate.civility || ""}
                    onValueChange={(v) => persistField("civility", v)}
                  >
                    <SelectTrigger className="rounded-none h-9 text-sm" data-testid="split-field-civility">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monsieur">M.</SelectItem>
                      <SelectItem value="madame">Mme</SelectItem>
                      <SelectItem value="mademoiselle">Mlle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-600">Nom</Label>
                  <Input
                    value={form.last_name ?? candidate.last_name ?? ""}
                    onChange={(e) => setField("last_name", upperLastName(e.target.value))}
                    onBlur={() => handleBlur("last_name")}
                    className="rounded-none text-sm h-9 font-bold uppercase"
                    data-testid="split-field-last-name"
                  />
                </div>
              </div>

              {/* Prefecture verification only for foreign nationals */}
              {(candidate.id_doc_type === "carte_sejour" || (candidate.nationality && !isEuEeaCh(candidate.nationality))) && (
                <label className="flex items-start gap-2 cursor-pointer p-2 mt-2 bg-blue-50 border border-blue-200" data-testid="split-prefecture">
                  <Checkbox
                    checked={!!form.prefecture_verified ?? !!candidate.prefecture_verified}
                    onCheckedChange={(v) => { setField("prefecture_verified", !!v); persistField("prefecture_verified", !!v); }}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="text-[11px] font-semibold text-[#3F3F3F]">🏛 Vérification préfecture effectuée</p>
                  </div>
                </label>
              )}

              <p className="text-[10px] text-gray-400 mt-4 italic">
                Les modifications sont sauvegardées automatiquement quand vous quittez un champ. Aucun bouton "Enregistrer" nécessaire.
              </p>
            </div>
          </aside>
        </div>
      </DialogContent>
      {/* CV Anonymization dialog (client-side canvas) */}
      <CVAnonymizer
        doc={current}
        candidateName={candidate.name}
        open={anonymizerOpen}
        onOpenChange={setAnonymizerOpen}
        onSaved={handleAnonymized}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (kept in this file to avoid a proliferation of tiny files)
// ---------------------------------------------------------------------------

/**
 * Inline rename control for the currently-viewed document.
 * The extension is preserved server-side; the input shows the base name only.
 */
function RenameDocBlock({ doc, onRenamed }) {
  const original = doc.original_filename || "";
  // Split off extension for display; the backend preserves it on save.
  const dotIdx = original.lastIndexOf(".");
  const initialBase = dotIdx > 0 ? original.slice(0, dotIdx) : original;
  const ext = dotIdx > 0 ? original.slice(dotIdx) : "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialBase);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  useEffect(() => {
    setValue(dotIdx > 0 ? original.slice(0, dotIdx) : original);
    setEditing(false);
  }, [doc.doc_id, original, dotIdx]);

  const cancel = () => { setValue(initialBase); setEditing(false); };
  const save = async () => {
    const cleaned = value.trim();
    if (!cleaned || cleaned === initialBase) { setEditing(false); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/documents/${doc.doc_id}/rename`, { name: cleaned });
      // Mutate the doc object in place so the parent's `current` reflects the change.
      doc.original_filename = r.data.original_filename;
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1200);
      setEditing(false);
      onRenamed?.(r.data.original_filename);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de renommer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 bg-[#FAFAFA] p-3" data-testid="rename-doc-block">
      <Label className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1 flex items-center gap-1">
        <FileText className="w-3 h-3" /> Nom du document
        {saving && <Loader2 className="w-3 h-3 animate-spin ml-1 text-[#662483]" />}
        {savedFlash && <Check className="w-3 h-3 ml-1 text-green-600" data-testid="rename-doc-saved" />}
      </Label>
      {editing ? (
        <div className="flex items-center gap-1">
          <Input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); else if (e.key === "Escape") cancel(); }}
            className="rounded-none text-sm h-8 flex-1"
            data-testid="rename-doc-input"
          />
          {ext && <span className="text-[10px] text-gray-400 select-none" data-testid="rename-doc-ext">{ext}</span>}
          <Button size="sm" onClick={save} disabled={saving} className="rounded-none h-8 bg-[#662483] hover:bg-[#552070] text-white px-3" data-testid="rename-doc-save">
            <Save className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="outline" onClick={cancel} className="rounded-none h-8 px-2" data-testid="rename-doc-cancel">
            <X className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="w-full text-left text-sm text-[#3F3F3F] hover:text-[#662483] underline decoration-dotted underline-offset-2 truncate"
          data-testid="rename-doc-trigger"
          title="Cliquer pour renommer"
        >
          {original}
        </button>
      )}
    </div>
  );
}

/**
 * Inline CACES expiry editor — shown when the currently-viewed document is a
 * `caces_habilitations`. Only rows for categories the admin has enabled
 * (`caces[key] === true`) are shown; the admin can edit the expiry date
 * per category and hit "Enregistrer" to persist in a single PATCH call.
 *
 * If no CACES is ticked yet, we display a short hint asking to enable
 * categories first from the CACES card below.
 */
function CacesExpiryInline({ candidate, onSaved }) {
  const [caces, setCaces] = useState(candidate?.caces || {});
  const [expiry, setExpiry] = useState(candidate?.caces_expiry || {});
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  useEffect(() => {
    setCaces(candidate?.caces || {});
    setExpiry(candidate?.caces_expiry || {});
  }, [candidate?.user_id, candidate?.caces, candidate?.caces_expiry]);

  const enabledKeys = CACES_CATEGORIES.filter((c) => caces[c.key]).map((c) => c.key);

  const toggleCategory = (key, on) => {
    setCaces((prev) => ({ ...prev, [key]: on }));
    if (!on) {
      setExpiry((prev) => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/candidates/${candidate.user_id}/admin-info`, {
        notes_admin: candidate.notes_admin || "",
        caces,
        caces_expiry: expiry,
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1400);
      onSaved?.(r.data);
      toast.success("Dates CACES enregistrées");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de sauvegarde CACES");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-amber-200 bg-amber-50 p-3" data-testid="caces-inline-block">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[10px] font-semibold uppercase tracking-wider text-amber-800 flex items-center gap-1">
          <Award className="w-3 h-3" /> Habilitations CACES — expirations
        </Label>
        {savedFlash && <Check className="w-3 h-3 text-green-600" data-testid="caces-inline-saved" />}
      </div>
      {enabledKeys.length === 0 && (
        <p className="text-[11px] text-amber-800 italic mb-2">
          Cochez les catégories CACES obtenues (bloc plus bas). Les dates de fin apparaîtront ici.
        </p>
      )}
      <div className="space-y-2 mb-3">
        {CACES_CATEGORIES.map((c) => {
          const checked = !!caces[c.key];
          return (
            <div key={c.key} className="flex items-center gap-2" data-testid={`caces-inline-row-${c.key}`}>
              <Checkbox
                checked={checked}
                onCheckedChange={(v) => toggleCategory(c.key, !!v)}
                data-testid={`caces-inline-check-${c.key}`}
              />
              <span className="text-[11px] font-semibold text-[#3F3F3F] w-8 shrink-0">{c.key}</span>
              <Input
                type="date"
                value={expiry[c.key] || ""}
                onChange={(e) => setExpiry((prev) => ({ ...prev, [c.key]: e.target.value }))}
                disabled={!checked}
                className="rounded-none text-xs h-7 flex-1"
                placeholder="Date d'expiration"
                data-testid={`caces-inline-date-${c.key}`}
              />
            </div>
          );
        })}
      </div>
      <Button
        onClick={save}
        disabled={saving}
        size="sm"
        className="rounded-none bg-amber-600 hover:bg-amber-700 text-white text-xs h-8 w-full"
        data-testid="caces-inline-save"
      >
        {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Enregistrement…</> : <><Save className="w-3 h-3 mr-1" /> Enregistrer les CACES</>}
      </Button>
    </div>
  );
}
